import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import * as Sentry from "@sentry/browser";
import { Box, Button, Typography } from "@mui/material";
import { LoadingProgressBar } from "@components";
import {
  completePortalCodeLogin,
  PortalLoginNotStartedError,
  takePortalLoginRetry,
} from "@helpers";
import { LoginPagesLayout } from "../LoginPagesLayout";
import { useLoginRoute } from "../useLoginRoute";

type CallbackResult = { continueUrl: string } | { restartLogin: true };

// Ariva hands the code over in the URL fragment; the query is the fallback.
const readCallbackParams = ({
  hash,
  search,
}: {
  hash: string;
  search: string;
}) => {
  const fragment = new URLSearchParams(hash.replace(/^#/, ""));
  const query = new URLSearchParams(search);
  return {
    code: fragment.get("code") ?? query.get("code"),
    state: fragment.get("state") ?? query.get("state"),
  };
};

export const AuthCallbackPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [{ code, state }] = useState(() => readCallbackParams(location));
  const { redirectToLogin } = useLoginRoute();

  const { data: result, error } = useQuery({
    queryKey: ["portalCodeLogin", code, state],
    queryFn: async (): Promise<CallbackResult> => {
      try {
        return { continueUrl: await completePortalCodeLogin({ code, state }) };
      } catch (e) {
        if (e instanceof PortalLoginNotStartedError && takePortalLoginRetry()) {
          return { restartLogin: true };
        }
        throw e;
      }
    },
    retry: false,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!result) return;

    if ("continueUrl" in result) {
      window.location.replace(result.continueUrl);
    } else {
      redirectToLogin({ replace: true });
    }
  }, [result, redirectToLogin]);

  useEffect(() => {
    if (error) {
      navigate({ search: "", hash: "" }, { replace: true });
      Sentry.captureException(error);
    }
  }, [error, navigate]);

  if (!error) {
    return <LoadingProgressBar />;
  }

  return (
    <LoginPagesLayout>
      <Box>
        <Typography variant="h4" mb={4}>
          {t("loginPage.portalCallback.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("loginPage.portalCallback.text")}
        </Typography>
      </Box>
      <Box>
        <Button variant="contained" onClick={() => redirectToLogin()}>
          {t("loginPage.portalCallback.button")}
        </Button>
      </Box>
    </LoginPagesLayout>
  );
};
